import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';
import { optimizeForProfile, optimizeForCover, compressImage } from '../utils/imageOptimization';

const MAX_UPLOAD_BYTES = 1024 * 1024; // 1MB after compression

const formatStorageError = (error) => {
  const code = error?.code || '';
  if (code === 'storage/quota-exceeded') {
    return 'Photo uploads are unavailable because Firebase Storage requires the Blaze (pay-as-you-go) plan. Upgrade the petfostering-7917d project in Firebase Console — usage under 5 GB stays free.';
  }
  if (code === 'storage/unauthorized') {
    return 'You do not have permission to upload this file.';
  }
  if (code === 'storage/canceled') {
    return 'Upload was cancelled.';
  }
  return error?.message || 'Upload failed';
};

const safeDelete = async (path) => {
  try {
    await deleteObject(ref(storage, path));
    return true;
  } catch (error) {
    if (error?.code === 'storage/object-not-found') return false;
    console.warn('Could not delete old file:', path, error.message);
    return false;
  }
};

const prepareImage = async (file, kind = 'profile') => {
  if (!file) throw new Error('No file provided');
  if (!file.type?.startsWith('image/')) {
    throw new Error('Please upload an image file (JPG, PNG, or WebP)');
  }

  let prepared;
  if (kind === 'cover') {
    prepared = await optimizeForCover(file);
  } else if (kind === 'gallery') {
    prepared = await compressImage(file, 1024, 1024, 0.82);
  } else {
    prepared = await optimizeForProfile(file);
  }

  if (prepared.size > MAX_UPLOAD_BYTES) {
    prepared = await compressImage(prepared, 800, 800, 0.7);
  }

  return prepared;
};

const uploadPreparedImage = async (path, file) => {
  const snapshot = await uploadBytes(ref(storage, path), file, {
    contentType: file.type || 'image/jpeg',
    cacheControl: 'public,max-age=31536000',
  });
  const url = await getDownloadURL(snapshot.ref);
  return { url, path: snapshot.ref.fullPath };
};

/**
 * Upload user profile picture (overwrites previous — one file per user)
 */
export const uploadProfilePicture = async (userId, file, previousPath = null) => {
  try {
    const prepared = await prepareImage(file, 'profile');
    const path = `profile-pictures/${userId}/avatar.jpg`;

    if (previousPath && previousPath !== path) {
      await safeDelete(previousPath);
    }
    await safeDelete(path);

    const result = await uploadPreparedImage(path, prepared);
    return { success: true, ...result };
  } catch (error) {
    console.error('Upload profile picture error:', error);
    return { success: false, error: formatStorageError(error) };
  }
};

/**
 * Upload pet picture (overwrites primary photo for pet)
 */
export const uploadPetPicture = async (userId, petId, file, previousPath = null) => {
  try {
    const prepared = await prepareImage(file, 'profile');
    const path = `pet-pictures/${userId}/${petId}/primary.jpg`;

    if (previousPath && previousPath !== path) {
      await safeDelete(previousPath);
    }
    await safeDelete(path);

    const result = await uploadPreparedImage(path, prepared);
    return { success: true, ...result };
  } catch (error) {
    console.error('Upload pet picture error:', error);
    return { success: false, error: formatStorageError(error) };
  }
};

/**
 * Upload profile gallery / cover photos with compression
 */
export const uploadProfilePhoto = async (userId, file, type = 'gallery', previousPath = null) => {
  try {
    let prepared;
    let path;

    if (type === 'cover') {
      prepared = await prepareImage(file, 'cover');
      path = `profiles/${userId}/cover.jpg`;
    } else if (type === 'profile') {
      prepared = await prepareImage(file, 'profile');
      path = `profiles/${userId}/profile.jpg`;
    } else {
      prepared = await prepareImage(file, 'gallery');
      path = `profiles/${userId}/gallery_${Date.now()}.jpg`;
    }

    if (previousPath) {
      await safeDelete(previousPath);
    }
    if (type === 'profile' || type === 'cover') {
      await safeDelete(path);
    }

    const result = await uploadPreparedImage(path, prepared);
    return { success: true, ...result };
  } catch (error) {
    console.error('Upload profile photo error:', error);
    return { success: false, error: formatStorageError(error) };
  }
};

/**
 * Upload receipt/document for offline job
 */
export const uploadReceipt = async (userId, jobId, file) => {
  try {
    if (!file) throw new Error('No file provided');

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size exceeds 5MB limit');
    }

    const path = `receipts/${userId}/${jobId}/${Date.now()}_${file.name.replace(/[^\w.-]/g, '_')}`;
    const result = await uploadPreparedImage(path, file);
    return { success: true, ...result };
  } catch (error) {
    console.error('Upload receipt error:', error);
    return { success: false, error: formatStorageError(error) };
  }
};

export const deleteFile = async (filePath) => {
  try {
    await deleteObject(ref(storage, filePath));
    return { success: true };
  } catch (error) {
    console.error('Delete file error:', error);
    return { success: false, error: formatStorageError(error) };
  }
};
